import { useEffect, useState } from "react";

import { extensionService } from "@/services/subscription/ExtensionService";
import type { PreapprovalResponse } from "@/pages/register/types";

/** Identificador do plano no billing — mesmo usado em `FluxExtensions` e `PostAttendanceContext`. */
const FLUXZAP_EXTENSION_PLAN_REF = "pos-atendimento";

/**
 * Referência opcional via env; quando ausente, usa `pos-atendimento`.
 */
const META_PLAN_REF = (
  import.meta.env.VITE_CRM_CLINICAL_META_EXTENSION_REF as string | undefined
)
  ?.trim()
  .toLowerCase() || FLUXZAP_EXTENSION_PLAN_REF;

export interface UseCrmSetupMetaExtensionPlanResult {
  plan: PreapprovalResponse | null;
  isLoading: boolean;
  errorMessage: string | null;
}

function findExtensionPlanByRef(
  rows: { plan: PreapprovalResponse }[],
  refKey: string,
): PreapprovalResponse | undefined {
  return rows.find(
    (row) =>
      String(row.plan?.external_reference ?? "")
        .toLowerCase()
        .trim() === refKey,
  )?.plan;
}

/**
 * Busca o plano da extensão FluxZap (`pos-atendimento`) para exibir CheckoutForm no setup CRM.
 */
export function useCrmSetupMetaExtensionPlan(
  enabled: boolean,
): UseCrmSetupMetaExtensionPlanResult {
  const [plan, setPlan] = useState<PreapprovalResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setPlan(null);
      setErrorMessage(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setIsLoading(true);
      setErrorMessage(null);
      try {
        const svc = new extensionService();
        const rows = await svc.getExtensionPlans();
        if (cancelled) return;

        const chosen = findExtensionPlanByRef(rows, META_PLAN_REF);

        if (!chosen) {
          setPlan(null);
          setErrorMessage(
            `Nenhum plano encontrado para a referência "${META_PLAN_REF}". Verifique se o plano FluxZap está cadastrado no billing.`,
          );
          return;
        }

        setPlan(chosen);
      } catch {
        if (!cancelled) {
          setPlan(null);
          setErrorMessage(
            "Não foi possível carregar os planos de extensão. Verifique a conexão.",
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { plan, isLoading, errorMessage };
}
